import { toUserMessage } from '../../lib/errors.js';
// SdiConfigPage — Configurazione integrazione SDI (provider e-fattura: Acube/Aruba).
// Permette: vedere stato config, salvare credenziali (provider + external_account_id),
// triggerare un fetch manuale per scaricare le fatture pendenti dal provider.
//
// La pagina è solo "scaffold sicuro": le credenziali del provider (es. password Aruba)
// devono essere caricate dall'admin via console Supabase con la RPC pgcrypto_encrypt
// (l'app non ha mai accesso al plaintext del webhook_secret_encrypted).

import {
  IonBackButton,
  IonBadge,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonListHeader,
  IonNote,
  IonPage,
  IonSelect,
  IonSelectOption,
  IonSpinner,
  IonText,
  IonTitle,
  IonToast,
  IonToolbar,
} from '@ionic/react';
import { downloadOutline, refreshOutline, shieldCheckmarkOutline } from 'ionicons/icons';
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../lib/auth/AuthProvider.js';
import { getSupabase, SUPABASE_URL } from '../../lib/supabase.js';

type Provider = 'acube' | 'aruba_develop';
type Status = 'pending' | 'active' | 'suspended' | 'revoked';

interface FiscalConfig {
  tenant_id: string;
  provider: Provider;
  external_account_id: string;
  status: Status;
  activated_at: string | null;
  last_poll_at: string | null;
  settings: Record<string, unknown>;
}

interface FetchReport {
  ok: boolean;
  tenants: number;
  report: Array<{
    tenant_id: string;
    provider: string;
    fetched: number;
    ingested: number;
    errors: string[];
  }>;
}

export function SdiConfigPage(): JSX.Element {
  const { profile } = useAuth();
  const tenantId = profile?.active_tenant_id ?? null;
  const qc = useQueryClient();

  const [provider, setProvider] = useState<Provider>('acube');
  const [externalId, setExternalId] = useState('');
  const [saving, setSaving] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [report, setReport] = useState<FetchReport | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const { data: config, isLoading, refetch } = useQuery({
    queryKey: ['fiscal_config', tenantId],
    enabled: !!tenantId,
    staleTime: 30_000,
    queryFn: async () => {
      const { data, error } = await getSupabase()
        .from('tenant_fiscal_config')
        .select('tenant_id,provider,external_account_id,status,activated_at,last_poll_at,settings')
        .eq('tenant_id', tenantId)
        .maybeSingle();
      if (error && error.code !== 'PGRST116') throw error;
      return data as FiscalConfig | null;
    },
  });

  // Pre-popola il form se config esiste
  if (config && externalId === '' && provider === 'acube') {
    setProvider(config.provider);
    setExternalId(config.external_account_id);
  }

  async function saveConfig(): Promise<void> {
    if (!tenantId) return;
    if (!externalId.trim()) { setToast('Inserisci l\'ID account esterno'); return; }
    setSaving(true);
    try {
      const payload = {
        tenant_id: tenantId,
        provider,
        external_account_id: externalId.trim(),
        status: 'pending' as Status,
      };
      const { error } = await getSupabase()
        .from('tenant_fiscal_config')
        .upsert(payload, { onConflict: 'tenant_id' });
      if (error) throw error;
      setToast('Configurazione salvata. Imposta il webhook secret cifrato dalla console.');
      void qc.invalidateQueries({ queryKey: ['fiscal_config', tenantId] });
    } catch (err) {
      setToast(toUserMessage(err));
    } finally {
      setSaving(false);
    }
  }

  async function triggerFetch(): Promise<void> {
    setFetching(true);
    setReport(null);
    try {
      const { data: sess } = await getSupabase().auth.getSession();
      const token = sess.session?.access_token;
      if (!token) throw new Error('Non autenticato');
      const res = await fetch(
        `${SUPABASE_URL.replace(/\/$/, '')}/functions/v1/sdi-fetch`,
        {
          method: 'POST',
          headers: {
            // T-192: il trigger manuale si autentica col JWT utente; l'edge
            // sdi-fetch processa solo i tenant del chiamante. Niente più token
            // interno nel client (prima `VITE_SDI_FETCH_TOKEN` lo bundlava → il
            // token interno era esposto nel client). Il token interno resta solo
            // per il cron (pg_cron service-role).
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );
      if (!res.ok) {
        const t = await res.text();
        throw new Error(`HTTP ${res.status}: ${t.slice(0, 200)}`);
      }
      const json = (await res.json()) as FetchReport;
      setReport(json);
      const myReport = json.report.find((r) => r.tenant_id === tenantId);
      if (myReport) {
        setToast(`Scaricate ${myReport.fetched}, importate ${myReport.ingested}${myReport.errors.length ? `, ${myReport.errors.length} errori` : ''}`);
      } else {
        setToast(`Job eseguito su ${json.tenants} tenant`);
      }
    } catch (err) {
      setToast(toUserMessage(err));
    } finally {
      setFetching(false);
    }
  }

  const statusColor = (s: Status | undefined): string =>
    s === 'active' ? 'success' : s === 'pending' ? 'warning' : s === 'suspended' || s === 'revoked' ? 'danger' : 'medium';

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start"><IonBackButton defaultHref="/settings" /></IonButtons>
          <IonTitle>Integrazione SDI</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={() => void refetch()} aria-label="Aggiorna">
              <IonIcon slot="icon-only" icon={refreshOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding" style={{ '--background': '#eef2f7' } as React.CSSProperties}>
        <IonText color="medium">
          <p style={{ fontSize: 13 }}>
            Configura l'intermediario SDI (Sistema di Interscambio) per ricevere automaticamente
            le fatture elettroniche emesse a tuo carico. Il job <code>sdi-fetch</code> gira ogni
            15 minuti e popola l'archivio fiscale.
          </p>
        </IonText>

        {isLoading ? (
          <IonSpinner name="dots" />
        ) : (
          <>
            <IonList inset>
              <IonListHeader><IonLabel>Stato</IonLabel></IonListHeader>
              <IonItem>
                <IonIcon icon={shieldCheckmarkOutline} slot="start" color={statusColor(config?.status) as 'success'} />
                <IonLabel>
                  <h3>Stato integrazione</h3>
                  {config ? (
                    <p style={{ fontSize: 12 }}>
                      Provider <b>{config.provider}</b> · Account <code>{config.external_account_id}</code>
                    </p>
                  ) : (
                    <p style={{ fontSize: 12 }}>Non configurato</p>
                  )}
                </IonLabel>
                {config && (
                  <IonBadge slot="end" color={statusColor(config.status)}>{config.status}</IonBadge>
                )}
              </IonItem>
              {config?.last_poll_at && (
                <IonItem>
                  <IonLabel>Ultimo poll</IonLabel>
                  <IonNote slot="end">{new Date(config.last_poll_at).toLocaleString('it-IT')}</IonNote>
                </IonItem>
              )}
              {config?.activated_at && (
                <IonItem>
                  <IonLabel>Attivata il</IonLabel>
                  <IonNote slot="end">{new Date(config.activated_at).toLocaleString('it-IT')}</IonNote>
                </IonItem>
              )}
            </IonList>

            <IonList inset>
              <IonListHeader><IonLabel>Configurazione provider</IonLabel></IonListHeader>
              <IonItem>
                <IonSelect
                  label="Provider" labelPlacement="stacked"
                  value={provider}
                  onIonChange={(e) => setProvider(e.detail.value as Provider)}
                >
                  <IonSelectOption value="acube">Acube</IonSelectOption>
                  <IonSelectOption value="aruba_develop">Aruba (DigitalHub)</IonSelectOption>
                </IonSelect>
              </IonItem>
              <IonItem>
                <IonInput
                  label="ID account esterno *" labelPlacement="stacked"
                  placeholder="es. acube_account_123"
                  value={externalId}
                  onIonInput={(e) => setExternalId(String(e.detail.value ?? ''))}
                />
              </IonItem>
              <IonItem lines="none">
                <IonNote style={{ fontSize: 11 }}>
                  Il <b>webhook secret</b> (per autenticare le notifiche entranti) va caricato
                  cifrato via SQL — chiama <code>pgcrypto_encrypt('tenant_fiscal_config',
                  'webhook_secret_encrypted', tenant_id, '&lt;secret&gt;')</code> dalla console.
                  L'app non vede mai il plaintext.
                </IonNote>
              </IonItem>
              <div style={{ padding: '0 16px 16px' }}>
                <IonButton expand="block" disabled={saving} onClick={() => void saveConfig()}>
                  {saving ? <IonSpinner name="dots" /> : 'Salva configurazione'}
                </IonButton>
              </div>
            </IonList>

            <IonList inset>
              <IonListHeader><IonLabel>Scarica fatture pendenti</IonLabel></IonListHeader>
              <IonItem lines="none">
                <IonText color="medium" style={{ fontSize: 12 }}>
                  Forza un poll immediato dal provider. Le nuove fatture finiscono in
                  Documenti → Fatture.
                </IonText>
              </IonItem>
              <div style={{ padding: '0 16px 16px' }}>
                <IonButton
                  expand="block"
                  disabled={fetching || !config || config.status !== 'active'}
                  onClick={() => void triggerFetch()}
                >
                  {fetching ? <IonSpinner name="dots" /> : (
                    <>
                      <IonIcon icon={downloadOutline} slot="start" />
                      Scarica ora
                    </>
                  )}
                </IonButton>
                {config && config.status !== 'active' && (
                  <IonNote style={{ fontSize: 11, display: 'block', textAlign: 'center', marginTop: 8 }}>
                    L'integrazione deve essere in stato <b>active</b>. Imposta status='active'
                    via console dopo il test del webhook.
                  </IonNote>
                )}
              </div>
              {report && (
                <IonItem color="light" lines="none">
                  <IonLabel className="ion-text-wrap">
                    <h3>Esito ultimo fetch</h3>
                    {report.report.map((r) => (
                      <p key={r.tenant_id} style={{ fontSize: 12 }}>
                        {r.provider}: <b>{r.fetched}</b> trovate, <b>{r.ingested}</b> importate
                        {r.errors.length > 0 && (
                          <span style={{ color: 'var(--ion-color-danger)' }}>
                            , {r.errors.length} errori: {r.errors.slice(0, 2).join('; ')}
                          </span>
                        )}
                      </p>
                    ))}
                  </IonLabel>
                </IonItem>
              )}
            </IonList>
          </>
        )}

        <IonToast
          isOpen={!!toast}
          message={toast ?? ''}
          duration={3500}
          onDidDismiss={() => setToast(null)}
        />
      </IonContent>
    </IonPage>
  );
}
